/**
 * Section 1 of the Credit Assessment Template 27.10.2026, Bank Statement Financial Analysis, from StatementIQ
 * (guidelines 3; procedure manual 1.1; plan phase 3). StatementIQ gives each statement's credits and debits per
 * month, so the averages still include transfers, loan proceeds and once-off items until the assessor adjusts them.
 */
import type { StatementCapture } from '../../../services/statementIq';

export const BUSY = ['UPLOADING', 'STORING', 'QUEUED', 'SUBMITTING', 'PROCESSING'];

export const statusLabel = (status: string) => status === 'READY' ? 'Ready'
  : BUSY.includes(status) ? 'Processing'
  : status === 'TIMED_OUT' ? 'Taking longer than expected'
  : status === 'SUBMISSION_UNKNOWN' ? 'Submission needs checking'
  : status === 'UPLOAD_FAILED' ? 'Upload failed' : 'Failed';

const average = (xs: number[]) => (xs.length ? Math.round((xs.reduce((s, x) => s + x, 0) / xs.length) * 100) / 100 : undefined);
const numbers = (xs: (number | undefined)[]) => xs.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));

export interface StatementFigures {
  /** Analysed statements with months */
  statements: number;
  months: number;
  /** Average monthly credits across every analysed month */
  inflows?: number;
  /** Average monthly debits across every analysed month */
  outflows?: number;
  returnedDebitOrders?: number;
  lowestBalance?: number;
}

/** One statement's own figures, for the table under Section 1 */
export function statementFigures(c: StatementCapture): StatementFigures {
  return figures(c.status === 'READY' ? [c] : []);
}

/** The averages Section 1 starts from: every month of every analysed statement, weighted equally */
export function figures(captures: StatementCapture[] = []): StatementFigures {
  const ready = captures.filter(c => c.status === 'READY' && c.result?.monthly_summary?.length);
  const months = ready.flatMap(c => c.result!.monthly_summary!.filter(m => m && typeof m === 'object'));
  const returned = numbers(months.map(m => m.returned_debit_orders));
  const lows = numbers(months.map(m => m.lowest_balance));
  return {
    statements: ready.length,
    months: months.length,
    inflows: average(numbers(months.map(m => m.total_credits))),
    outflows: average(numbers(months.map(m => m.total_debits))),
    returnedDebitOrders: returned.length ? returned.reduce((s, x) => s + x, 0) : undefined,
    lowestBalance: lows.length ? Math.min(...lows) : undefined,
  };
}
